import { call, put, takeEvery, takeLatest, delay, race, take, debounce } from 'redux-saga/effects';
import AsyncActionControl from 'react-redux-await-control/dist/AsyncActionControl';
import * as Sentry from '@sentry/react';

type RequestConfig = {
  retry?: number;
  type?: 'debounce' | 'latest' | 'default';
  debounceTime?: number;
  retryDelay?: number;
};

type RetryConfig = Required<Pick<RequestConfig, 'retry' | 'retryDelay'>>;

type RequestAction = { payload?: unknown; meta?: Record<string, unknown> };

type RequestContext = {
  executor: (payload: unknown) => unknown;
  validator: (result: unknown) => boolean;
  beforeRequest?: () => void;
  additionalPayload: Record<string, unknown>;
  additionalMeta: Record<string, unknown>;
  onSuccess?: (result: unknown, payload: unknown, meta: unknown) => unknown;
  onError?: (error: unknown, payload: unknown, meta: unknown) => unknown;
  responseParser: (result: unknown, payload: unknown, meta: unknown) => unknown;
  errorParser: (error: unknown) => unknown;
};

type RequestArgs = {
  payload: unknown;
  meta: Record<string, unknown>;
  customPayload: unknown;
  customMeta: unknown;
};

type AttemptState = { attempts: number; result: unknown };

const RETRY_DELAY = 1_500;

const identity = (value: unknown) => value;
const isPresent = (value: unknown) => !!value;

const readContext = (asyncAction: AsyncActionControl): RequestContext => ({
  executor: asyncAction.getContext('request'),
  validator: asyncAction.getContext('resultValidator') || isPresent,
  beforeRequest: asyncAction.getContext('beforeRequest'),
  additionalPayload: asyncAction.getContext('additionalPayload') || {},
  additionalMeta: asyncAction.getContext('additionalMeta') || {},
  onSuccess: asyncAction.getContext('onRequestSuccess'),
  onError: asyncAction.getContext('onRequestError'),
  responseParser: asyncAction.getContext('responseParser') || identity,
  errorParser: asyncAction.getContext('errorParser') || identity,
});

const mergePayload = (payload: unknown, extra: Record<string, unknown>) =>
  typeof payload === 'object' ? { ...payload, ...extra } : payload;

const mergeMeta = (meta: unknown, extra: Record<string, unknown>, payload: unknown) =>
  typeof meta === 'object' ? { ...meta, ...extra, payload } : meta;

const getStatusCode = (error: unknown) => (error as { response?: { status?: number } })?.response?.status;

const getBaseURL = (error: unknown) => (error as { config?: { baseURL?: string } })?.config?.baseURL;

const isServerError = (statusCode?: number) => !!statusCode?.toString().startsWith('5');

const describeHandlerError = (error: unknown) => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
};

const reportHandlerError = (asyncAction: AsyncActionControl, stage: string, error: unknown) => {
  const action = asyncAction.start.toString();
  const summary = `[takeRequest] ${stage} de ${action} lançou e foi isolado: ${describeHandlerError(error)}`;
  console.warn(summary);
  Sentry.captureMessage(summary, { level: 'warning', tags: { feature: 'take-request', stage }, extra: { action } });
};

const runSafely = <T>(asyncAction: AsyncActionControl, stage: string, fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch (error) {
    reportHandlerError(asyncAction, stage, error);
    return fallback;
  }
};

const parseResponse = (
  asyncAction: AsyncActionControl,
  context: RequestContext,
  args: RequestArgs,
  result: unknown,
) => {
  try {
    return context.responseParser(result, args.customPayload, args.customMeta);
  } catch (error) {
    reportHandlerError(asyncAction, 'responseParser', error);
    throw error;
  }
};

function* emitSuccess(asyncAction: AsyncActionControl, context: RequestContext, args: RequestArgs, result: unknown) {
  const parsedResult = parseResponse(asyncAction, context, args, result);
  yield put(asyncAction.success(parsedResult, args.customMeta));
  if (context.onSuccess) {
    try {
      yield call(context.onSuccess, parsedResult, args.customPayload, args.customMeta);
    } catch (error) {
      reportHandlerError(asyncAction, 'onRequestSuccess', error);
    }
  }
}

function* emitFailure(
  asyncAction: AsyncActionControl,
  context: RequestContext,
  args: RequestArgs,
  error: unknown,
  statusCode?: number,
) {
  const parsedError = runSafely(asyncAction, 'errorParser', () => context.errorParser(error), undefined);
  yield put(asyncAction.failure(parsedError, { ...args.meta, statusCode, baseURL: getBaseURL(error) }));
  if (context.onError) {
    try {
      yield call(context.onError, error, args.payload, args.meta);
    } catch (handlerError) {
      reportHandlerError(asyncAction, 'onRequestError', handlerError);
    }
  }
}

function* handleError(
  asyncAction: AsyncActionControl,
  context: RequestContext,
  config: RetryConfig,
  args: RequestArgs,
  state: AttemptState,
  error: unknown,
) {
  const statusCode = getStatusCode(error);

  if (isServerError(statusCode)) {
    state.attempts += 1;
    yield delay(config.retryDelay);
  } else {
    state.attempts = config.retry;
  }

  if (state.attempts === config.retry) {
    yield* emitFailure(asyncAction, context, args, error, statusCode);
  }
}

function* runAttempt(
  asyncAction: AsyncActionControl,
  context: RequestContext,
  config: RetryConfig,
  args: RequestArgs,
  state: AttemptState,
) {
  try {
    state.result = yield call(context.executor, args.customPayload);
    if (!context.validator(state.result)) {
      state.attempts += 1;
      yield delay(config.retryDelay);
    }

    if (state.attempts === config.retry || context.validator(state.result)) {
      yield* emitSuccess(asyncAction, context, args, state.result);
    }
  } catch (error) {
    yield* handleError(asyncAction, context, config, args, state, error);
  }
}

function* request(asyncAction: AsyncActionControl, config: RetryConfig, { payload, meta = {} }: RequestAction) {
  const context = readContext(asyncAction);
  const args: RequestArgs = {
    payload,
    meta,
    customPayload: mergePayload(payload, context.additionalPayload),
    customMeta: mergeMeta(meta, context.additionalMeta, payload),
  };

  if (context.beforeRequest) {
    runSafely(asyncAction, 'beforeRequest', context.beforeRequest, undefined);
  }

  const state: AttemptState = { attempts: 0, result: null };

  while (config.retry > state.attempts && !context.validator(state.result)) {
    yield* runAttempt(asyncAction, context, config, args, state);
  }
}

export default function takeRequest(asyncAction: AsyncActionControl, config: RequestConfig = {}) {
  const { retry = 3, retryDelay = RETRY_DELAY, type = 'default', debounceTime = 300 } = config;
  const retryConfig: RetryConfig = { retry, retryDelay };

  function* sagaControl(action: RequestAction) {
    yield race({
      task: call(request, asyncAction, retryConfig, action),
      cancel: take(asyncAction.cancel.toString()),
    });
  }

  if (type === 'debounce') {
    return debounce(debounceTime, asyncAction.start.toString(), sagaControl);
  }

  if (type === 'latest') {
    return takeLatest(asyncAction.start.toString(), sagaControl);
  }

  return takeEvery(asyncAction.start.toString(), sagaControl);
}
